import { useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { useQueryState } from 'nuqs';
import { billing } from '@/services/billing';
import { useBuilderBalance } from '@/features/billing-usage';
import { useCreditPurchase } from './use-credit-purchase';
import { useCreditsPurchasePageName } from './use-credits-purchase-page-name';

/** The parameter that holds a purchase modal open. */
export const PURCHASE_MODAL_PARAM = 'purchaseModal';
const AI_CREDITS = 'ai-credits';
const ACCOUNT_FUNDS = 'account-funds';

/**
 * `?purchaseModal=` holds a purchase modal open: `ai-credits` on a plan that buys AI credits (not
 * Builder or Flex), and `account-funds` on Builder. The workspace-home balance card, the app-header banner and the
 * embedded app's setup checklist all link to it.
 *
 * The parameter is state rather than an event. It says which modal is open, so it is taken out when
 * that modal closes rather than when it is read. The same request can therefore be made again, a
 * reload keeps the modal, and nothing has to tell a value read twice from a value asked for twice —
 * because the value is only ever read.
 *
 * Mounted by the billing shell, so the link works on whichever billing tab it lands on. It answers
 * for its own two values only: `inbox-previews` belongs to the Inbox previews row, which holds it
 * open the same way.
 *
 * Waits for billing to initialize and the Builder balance to land: the gating it reads, and the
 * balance the Builder modal caps against, are not accurate until then.
 */
export function usePurchaseModalDeepLink(): void {
  const [requested, setRequested] = useQueryState(PURCHASE_MODAL_PARAM);
  const isInitialized = useStore(billing.initialized);
  const isBuilderPlan = useStore(billing.isBuilderPlan);
  const usesCredits = useStore(billing.usesCredits);
  const { isLoading: isBalanceLoading } = useBuilderBalance();
  const pageName = useCreditsPurchasePageName();
  const purchase = useCreditPurchase({ pageName });

  // The value a modal is open for. Real state, not a guess: the parameter stays put for as long as
  // the modal is up, so this is what says the request has already been answered.
  const openFor = useRef<string | null>(null);

  const isReady = isInitialized && !isBalanceLoading;
  // What the requested value opens, or nothing when this account cannot buy it.
  let openRequested: (() => Promise<void>) | undefined;
  if (requested === AI_CREDITS && !usesCredits && !purchase.aiCredits.isPurchaseDisabled) {
    openRequested = purchase.openAiCredits;
  } else if (requested === ACCOUNT_FUNDS && isBuilderPlan && !purchase.builderCredits.isPurchaseDisabled) {
    openRequested = () => purchase.openBuilderTopUp();
  }

  useEffect(() => {
    if (!isReady || openRequested == null || openFor.current != null) {
      return;
    }

    openFor.current = requested;
    void openRequested().finally(() => {
      openFor.current = null;
      // The modal has closed, so the parameter that held it open goes with it.
      void setRequested(null);
    });
  }, [isReady, openRequested, requested, setRequested]);
}
